import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUp, LogOut } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Eyebrow, Reveal, EASE } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/components/AuthModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDERS = [
    { id: "openai", label: "ChatGPT" },
    { id: "gemini", label: "Gemini" },
];

const SUGGESTIONS = [
    "Style a black blazer for a gallery night",
    "Build a capsule wardrobe in cool grey tones",
];

export default function Account() {
    const { user, setUser, signOut } = useAuth();
    const navigate = useNavigate();
    const [provider, setProvider] = useState("openai");
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [taste, setTaste] = useState({ favourite_pieces: "", favourite_brands: "", style_notes: "" });

    useEffect(() => {
        if (user === false) navigate("/", { replace: true });
    }, [user, navigate]);

    useEffect(() => {
        if (user?.user_id) {
            axios
                .get(`${API}/ai/history`, { withCredentials: true })
                .then(({ data }) => setMessages(data))
                .catch(() => {});
            axios
                .get(`${API}/taste`, { withCredentials: true })
                .then(({ data }) =>
                    setTaste({
                        favourite_pieces: data.favourite_pieces || "",
                        favourite_brands: data.favourite_brands || "",
                        style_notes: data.style_notes || "",
                    }),
                )
                .catch(() => {});
        }
    }, [user?.user_id]);

    if (!user) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-white">
                <p className="text-sm font-extralight tracking-wide text-slate-400">Loading…</p>
            </main>
        );
    }

    const displayName = user.name || user.email.split("@")[0];

    const joinWaitlist = async () => {
        try {
            await axios.post(`${API}/waitlist`, { email: user.email, consent: true });
            setUser({ ...user, waitlist_member: true });
            toast.success("You're on the list. Welcome to EDIT.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    const saveTaste = async () => {
        try {
            await axios.put(`${API}/taste`, taste, { withCredentials: true });
            toast.success("Taste profile saved — the stylist will use it.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    const send = async (e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text || busy) return;
        setBusy(true);
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", provider }]);
        try {
            const res = await fetch(`${API}/ai/stylist`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ message: text, provider }),
            });
            if (!res.ok) {
                const detail = await res.json().catch(() => null);
                toast.error(formatApiErrorDetail(detail?.detail) || "The stylist is unavailable right now.");
                setMessages((m) => m.slice(0, -1));
                setBusy(false);
                return;
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || "";
                for (const part of parts) {
                    const line = part.replace(/^data:\s*/, "").trim();
                    if (!line) continue;
                    const evt = JSON.parse(line);
                    if (evt.error) {
                        toast.error(evt.error);
                    } else if (evt.t) {
                        setMessages((m) => {
                            const copy = m.slice();
                            const last = copy[copy.length - 1];
                            if (last && last.role === "assistant") {
                                copy[copy.length - 1] = { ...last, content: last.content + evt.t };
                            }
                            return copy;
                        });
                    }
                }
            }
        } catch {
            toast.error("The stylist is unavailable right now.");
            setMessages((m) => m.slice(0, -1));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="min-h-screen bg-white text-slate-950" data-testid="account-page">
            <Nav />
            <div className="mx-auto max-w-3xl px-6 pb-24 pt-40 sm:px-10 md:pt-48">
                <Reveal>
                    <Eyebrow>Your EDIT ID</Eyebrow>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="mt-7 text-5xl font-extralight tracking-[-0.03em]">
                        Welcome back, {displayName}.
                    </h1>
                </Reveal>
                <Reveal delay={0.14}>
                    <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="account-meta">
                        <span className="rounded-full border border-slate-200 px-4 py-2 text-xs font-light text-slate-600" data-testid="account-email">
                            {user.email}
                        </span>
                        <span className="rounded-full border border-slate-200 px-4 py-2 text-xs font-light capitalize text-slate-600">
                            {user.provider === "google" ? "Google account" : "Email account"}
                        </span>
                        {user.waitlist_member ? (
                            <span className="flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-xs font-light text-white" data-testid="waitlist-status-chip">
                                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                On the waitlist
                            </span>
                        ) : (
                            <button
                                onClick={joinWaitlist}
                                data-testid="account-join-waitlist-button"
                                className="rounded-full border border-slate-950/15 bg-white/60 px-4 py-2 text-xs font-light text-slate-950 transition-colors duration-300 hover:bg-slate-50"
                            >
                                Join the waitlist
                            </button>
                        )}
                        <button
                            onClick={async () => {
                                await signOut();
                                navigate("/");
                            }}
                            data-testid="account-signout-button"
                            className="flex items-center gap-2 rounded-full px-4 py-2 text-xs font-light text-slate-400 transition-colors duration-300 hover:text-slate-950"
                        >
                            <LogOut size={13} strokeWidth={1.5} />
                            Sign out
                        </button>
                    </div>
                </Reveal>

                <Reveal delay={0.16}>
                    <section className="mt-14 rounded-[32px] border border-slate-100 p-6 sm:p-10" data-testid="taste-panel">
                        <h2 className="text-2xl font-extralight tracking-tight">
                            Your taste, <span className="italic font-light text-slate-950">remembered.</span>
                        </h2>
                        <p className="mt-3 max-w-md text-sm font-extralight leading-relaxed text-slate-500">
                            Tell the stylist what you love — it personalises every answer with your favourites.
                        </p>
                        <div className="mt-7 grid gap-6 sm:grid-cols-2">
                            <input
                                value={taste.favourite_pieces}
                                onChange={(e) => setTaste({ ...taste, favourite_pieces: e.target.value })}
                                placeholder="Favourite pieces — e.g. oversized blazer"
                                data-testid="taste-pieces-input"
                                className="w-full border-b border-slate-300 bg-transparent py-3 text-sm font-extralight outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
                            />
                            <input
                                value={taste.favourite_brands}
                                onChange={(e) => setTaste({ ...taste, favourite_brands: e.target.value })}
                                placeholder="Favourite brands"
                                data-testid="taste-brands-input"
                                className="w-full border-b border-slate-300 bg-transparent py-3 text-sm font-extralight outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
                            />
                        </div>
                        <input
                            value={taste.style_notes}
                            onChange={(e) => setTaste({ ...taste, style_notes: e.target.value })}
                            placeholder="Style notes — e.g. cool tones only, minimal jewellery"
                            data-testid="taste-notes-input"
                            className="mt-6 w-full border-b border-slate-300 bg-transparent py-3 text-sm font-extralight outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
                        />
                        <button
                            onClick={saveTaste}
                            data-testid="taste-save-button"
                            className="mt-7 rounded-full bg-slate-950 px-7 py-3 text-sm font-light tracking-wide text-white transition-colors duration-300 hover:bg-slate-800 active:scale-95"
                        >
                            Save taste profile
                        </button>
                    </section>
                </Reveal>

                <Reveal delay={0.2}>
                    <section className="mt-16 rounded-[32px] bg-[#F8F9FA] p-6 sm:p-10" data-testid="stylist-panel">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <h2 className="text-2xl font-extralight tracking-tight">
                                The stylist, <span className="italic font-light text-slate-950">yours.</span>
                            </h2>
                            <div className="flex gap-1 rounded-full bg-white p-1" data-testid="stylist-provider-toggle">
                                {PROVIDERS.map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => setProvider(p.id)}
                                        data-testid={`stylist-provider-${p.id}`}
                                        className={`rounded-full px-4 py-2 text-xs font-light transition-colors duration-300 ${
                                            provider === p.id ? "bg-slate-950 text-white" : "text-slate-500"
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-6 flex max-h-[420px] min-h-[180px] flex-col gap-3 overflow-y-auto" data-testid="stylist-messages">
                            {messages.length === 0 && (
                                <div className="flex flex-col items-start gap-3 py-6">
                                    <p className="text-sm font-extralight leading-relaxed text-slate-500">
                                        Ask EDIT's stylist anything — or start here:
                                    </p>
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setInput(s)}
                                            data-testid="stylist-suggestion"
                                            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-light text-slate-600 transition-colors duration-300 hover:text-slate-950"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm font-extralight leading-relaxed ${
                                        m.role === "user"
                                            ? "self-end bg-slate-950 text-white"
                                            : "self-start bg-white text-slate-700"
                                    }`}
                                    data-testid={m.role === "user" ? "stylist-user-message" : "stylist-assistant-message"}
                                >
                                    {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                                </div>
                            ))}
                        </div>

                        <form
                            onSubmit={send}
                            className="mt-6 flex items-center gap-2 rounded-full border border-slate-200 bg-white p-2 pl-6 transition-colors duration-300 focus-within:border-slate-900"
                            data-testid="stylist-form"
                        >
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask the stylist…"
                                aria-label="Ask the stylist"
                                data-testid="stylist-input"
                                className="w-full min-w-0 flex-1 bg-transparent py-2.5 text-sm font-extralight outline-none placeholder:text-slate-300"
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="stylist-send-button"
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-950 text-white transition-colors duration-300 hover:bg-slate-800 disabled:opacity-50"
                                aria-label="Send"
                            >
                                <ArrowUp size={16} strokeWidth={1.5} />
                            </button>
                        </form>
                    </section>
                </Reveal>
            </div>
            <Footer />
        </main>
    );
}
